import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { useState } from 'react'

interface ConfirmDialogProps {
  trigger: React.ReactNode
  title: string
  description: string
  confirmLabel?: string
  loadingLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'destructive'
  onConfirm: () => void | Promise<void>
}

export function ConfirmDialog({
  trigger,
  title,
  description,
  confirmLabel = 'Confirm',
  loadingLabel,
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
}: ConfirmDialogProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const resolvedLoadingLabel = loadingLabel ?? (() => {
    switch (confirmLabel.trim().toLowerCase()) {
      case 'delete':
        return 'Deleting...'
      case 'reset':
        return 'Resetting...'
      case 'restart':
        return 'Restarting...'
      case 'disconnect':
        return 'Disconnecting...'
      case 'unlink':
        return 'Unlinking...'
      case 'revoke':
        return 'Revoking...'
      case 'stop':
        return 'Stopping...'
      case 'remove':
        return 'Removing...'
      case 'impersonate':
        return 'Impersonating...'
      default:
        return variant === 'destructive' ? 'Working...' : 'Working...'
    }
  })()

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await onConfirm()
      setOpen(false)
    } catch {
      // Keep the dialog open so the caller can surface the error and the user can retry or cancel.
    } finally {
      setLoading(false)
    }
  }

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (loading && !nextOpen) {
          return
        }
        setOpen(nextOpen)
      }}
    >
      <AlertDialog.Trigger asChild>{trigger}</AlertDialog.Trigger>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <AlertDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg p-6 w-full max-w-md z-50 shadow-lg">
          <AlertDialog.Title className="text-lg font-semibold mb-2">
            {title}
          </AlertDialog.Title>
          <AlertDialog.Description className="text-muted-foreground mb-6">
            {description}
          </AlertDialog.Description>
          <div className="flex justify-end gap-3">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
                disabled={loading}
              >
                {cancelLabel}
              </button>
            </AlertDialog.Cancel>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                variant === 'destructive'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              {loading ? resolvedLoadingLabel : confirmLabel}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
